
import { useCallback, useReducer } from "react";
import { ViewOptionStateEnum } from "../../components/contextual-app-bar/ViewOptionStateEnum";
import { CustomDurationDialogValues } from "../../components/custom-duration-dialog/CustomDurationDialog";
import CustomDurationUtils, { DurationOptionEnum } from "../../components/custom-duration-dialog/CustomDurationUtils";
import { addHours } from "date-fns";
import _ from "lodash";
import { GridData } from "../../components/ag-grid-extensions/models/GridData";
import { disabledRecipientsReportActionItems } from "./DisabledRecipientsReportActions";
import { GridApi } from "ag-grid-community";
import { ActionMenuItem } from "../../components/action-menu/ActionMenuItem";
import { sortAndDispatchActions } from "../../common/utilities/SortAndDispatchActions";

export type DisabledRecipientsReportStateType = {
    viewOptionState: ViewOptionStateEnum,
    organizationID: number | undefined,
    durationLabel: string,
    durationOptions: CustomDurationDialogValues;
    gridData: GridData | undefined;
    gridApi: GridApi | undefined;
    actions: ActionMenuItem[];
    downloadUid: string | undefined;
};

export const defaultDisabledRecipientsReportState: DisabledRecipientsReportStateType = {
    viewOptionState: ViewOptionStateEnum.Default,
    organizationID: undefined,
    gridApi: undefined,
    durationLabel: CustomDurationUtils.getCustomDurationLabel(DurationOptionEnum.OneDay, true),
    durationOptions: {
        option: DurationOptionEnum.OneDay,
        duration: {
            startDate: addHours(new Date(), -24),
            endDate: new Date()
        }
    },
    gridData: undefined,
    actions: [...disabledRecipientsReportActionItems],
    downloadUid: undefined
};

function getDefaultDisabledRecipientsReportState() : DisabledRecipientsReportStateType {
    const durationOptions: CustomDurationDialogValues = {...defaultDisabledRecipientsReportState.durationOptions}
    durationOptions.duration.startDate = addHours(new Date(), -24);
    durationOptions.duration.endDate = new Date();
    return {...defaultDisabledRecipientsReportState,
        durationOptions: durationOptions
    };
}
enum DisabledRecipientsReportStateActionType {

    OrganizationChanged = 'DISABLED_RECIPIENTS_REPORT_ORGANIZATION_CHANGED',
    DurationChanged = 'DISABLED_RECIPIENTS_REPORT_DURATION_CHANGED',
    GridDataChanged = 'DISABLED_RECIPIENTS_REPORT_GRID_DATA_CHANGED',
    ActionsChanged = 'DISABLED_RECIPIENTS_REPORT_ACTIONS_CHANGED',
    DownloadUidChanged = 'DISABLED_RECIPIENTS_REPORT_DOWNLOAD_UID_CHANGED',
    GridApiChanged = 'DISABLED_RECIPIENTS_REPORT_GRID_API_CHANGED',    
    ResetChanged = 'DISABLED_RECIPIENTS_REPORT_RESET_CHANGED'
}

type DisabledRecipientsReportStateActions = {
    type: DisabledRecipientsReportStateActionType.OrganizationChanged;
    payload: number;
} | {
    type: DisabledRecipientsReportStateActionType.DurationChanged;
    payload: CustomDurationDialogValues;
} | {
    type: DisabledRecipientsReportStateActionType.GridDataChanged;
    payload: GridData;
} | {
    type: DisabledRecipientsReportStateActionType.GridApiChanged;
    payload: GridApi;
} | {
    type: DisabledRecipientsReportStateActionType.ActionsChanged;
    payload: ActionMenuItem[];
} | {
    type: DisabledRecipientsReportStateActionType.DownloadUidChanged;
    payload: string | undefined;
} | {
    type: DisabledRecipientsReportStateActionType.ResetChanged;
    payload: void;
};


const optionsChanged = (newState: DisabledRecipientsReportStateType, 
    defaultState: DisabledRecipientsReportStateType) => 
    newState.durationOptions.option !== defaultState.durationOptions.option;

function disabledRecipientsReportStateReducer(state: DisabledRecipientsReportStateType, action: DisabledRecipientsReportStateActions): DisabledRecipientsReportStateType {

    const copy = _.cloneDeep(state);

    switch (action.type) {
        
        case DisabledRecipientsReportStateActionType.OrganizationChanged:
            copy.organizationID = action.payload;
            return copy;

        case DisabledRecipientsReportStateActionType.GridDataChanged:
            copy.gridData = action.payload;
            return copy;

        case DisabledRecipientsReportStateActionType.GridApiChanged:
            copy.gridApi = action.payload;
            return copy;
    

        case DisabledRecipientsReportStateActionType.DownloadUidChanged:
            copy.downloadUid = action.payload;
            return copy;
    
        case DisabledRecipientsReportStateActionType.ActionsChanged:
            copy.actions = action.payload;
            return copy;

        case DisabledRecipientsReportStateActionType.DurationChanged:

            copy.durationLabel = CustomDurationUtils.getCustomDurationLabel(action.payload.option, true);
            copy.durationOptions =  {
                option: action.payload.option,
                duration: {
                    startDate: new Date(action.payload.duration.startDate as Date),
                    endDate: new Date(action.payload.duration.endDate as Date)
                }
            };

            copy.viewOptionState = optionsChanged(copy, defaultDisabledRecipientsReportState) ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;

            return copy;

               
        case DisabledRecipientsReportStateActionType.ResetChanged:
            return getDefaultDisabledRecipientsReportState();

        default:
            return state;
    }
}

export default function useDisabledRecipientsReportState() {

    const [state, dispatch] = useReducer(disabledRecipientsReportStateReducer, getDefaultDisabledRecipientsReportState());

    const setOrganization = useCallback((value: number) => dispatch({
        type: DisabledRecipientsReportStateActionType.OrganizationChanged,
        payload: value
    }), []);

    const setDuration = useCallback((value: CustomDurationDialogValues) => dispatch({
        type: DisabledRecipientsReportStateActionType.DurationChanged,
        payload: value
    }), []);

    const setGridData = useCallback((value: GridData) => dispatch({
        type: DisabledRecipientsReportStateActionType.GridDataChanged,
        payload: value
    }), []);

    // const setActions = useCallback((value: ActionMenuItem[]) => dispatch({
    //     type: DisabledRecipientsReportStateActionType.ActionsChanged,
    //     payload: value
    // }), []);

    const setActions = useCallback((actions: ActionMenuItem[]) => sortAndDispatchActions(
        state.actions,
        actions,
        (sortedActions) => dispatch({
            type: DisabledRecipientsReportStateActionType.ActionsChanged,
            payload: sortedActions
        })
    ), [state.actions]);

    const setDownloadUid = useCallback((value: string | undefined) => dispatch({
        type: DisabledRecipientsReportStateActionType.DownloadUidChanged,
        payload: value
    }), []);

    const setGridApi = useCallback((value: GridApi) => dispatch({
        type: DisabledRecipientsReportStateActionType.GridApiChanged,
        payload: value
    }), []);


    const resetState = useCallback(() => dispatch({
        type: DisabledRecipientsReportStateActionType.ResetChanged,
        payload: undefined
    }), []);

    return {
        state,
        setOrganization,
        setGridData,
        setActions,
        setDownloadUid,
        setGridApi,
        setDuration,
        resetState
    };
}

